import {
  BackspaceOutlined,
  BarChartOutlined,
  ChatOutlined,
  CloudSync,
  DeleteOutline,
  EditOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  LaunchOutlined,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Can, usePermission } from "src/helpers/Auth";
import {
  ClearDatasetModal,
  CreateDatasetFolderModal,
  CreateDatasetModal,
  DatasetFolderMappingModal,
  UpdateDatasetFolderModal,
} from "src/views/forms/DatasetModals";
import {
  searchDatasets,
  useDownloadDatasetFile,
  useStarDataset,
  useSyncRemoteDataset,
  useUnstarDataset,
} from "../services/DatasetServices";

import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import { useHistory } from "react-router-dom";
import { nuclearn_api_url } from "src/App";
import { CustomNoRowsOverlay } from "src/reusable/components/CustomNoRowsOverlay";
import DataGridSearch from "src/reusable/components/DataGridSearch";
import { PowerBIDataModal } from "src/reusable/components/PowerBIComponents";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { DeleteDatasetModal } from "src/views/forms/datasetModels/DeleteDatasetModal";
import { UpdateDatasetModal } from "src/views/forms/datasetModels/UpdateDatasetModal";
import { CreateItemButton } from "src/views/forms/FormUtils";
import DatasetFilters from "./DatasetFilters";
import DatasetTypeChip from "./DatasetTypeChip";

interface DatasetTableSearchProps {
  atomResults?: boolean;
}

const DatasetStarCell = ({ dataset }: { dataset: any }) => {
  const starMutation = useStarDataset();
  const unstarMutation = useUnstarDataset();

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dataset?.is_starred_by_user) {
      unstarMutation.mutate(dataset?.id);
    } else {
      starMutation.mutate(dataset?.id);
    }
  };

  return (
    <Tooltip
      title={
        dataset?.is_starred_by_user
          ? "Remove from favorites"
          : "Add to favorites"
      }
      arrow
    >
      <IconButton
        size="small"
        onClick={handleStarClick}
        data-testid={`ds-btn-star-${dataset?.id}`}
        data-attr={`datasets:btn:toggle-star-${dataset?.id}`}
        sx={{
          color: dataset?.is_starred_by_user ? "warning.main" : "action.active",
        }}
      >
        {dataset?.is_starred_by_user ? (
          <StarIcon fontSize="small" />
        ) : (
          <StarBorderIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const DatasetTableSearch = ({
  atomResults = false,
}: DatasetTableSearchProps) => {
  const [currentDataset, setCurrentDataset] = useState<any>({});
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openClearModal, setOpenClearModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openFolderMappingModal, setOpenFolderMappingModal] = useState(false);
  const [openPowerBIModal, setOpenPowerBIModal] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    id: false,
  });
  const history = useHistory();
  const theme = useTheme();
  const { setDropdownDatasets } = useChatStore();

  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLg = useMediaQuery(theme.breakpoints.down(1201));
  const isXl = useMediaQuery(theme.breakpoints.down("xl"));

  const { mutate: downloadDatasetFile } = useDownloadDatasetFile();

  const isDocumentType = (type: string) => {
    return type === "document";
  };
  const handleFolderMappingDataset = useCallback((dataset: any) => {
    setCurrentDataset(dataset);
    setOpenFolderMappingModal(true);
  }, []);

  const handleUploadToDataset = useCallback(
    (id: string) => () => {
      history.push(`/datasets/${id}/upload`);
    },
    [history],
  );

  const handleDownloadDataset = useCallback(
    (params: any) => () => {
      downloadDatasetFile({
        datasetId: params.id,
        datasetType: params.dataset_type,
        datasetName: params?.name,
      });
    },
    [downloadDatasetFile],
  );

  const handleAnalyzeClick = useCallback(
    (id: string) => () => {
      history.push(`/datasets/${id}/stats`);
    },
    [history],
  );

  const navigateToDataset = useCallback(
    (row: any) => {
      const isFolder = Boolean(row?.row.isFolder);
      const folderId = isFolder ? row?.row.id : row?.row.dataset_folder_fk;
      if (isFolder) {
        return history.push(`/datasets/dataset-folders/${folderId}`, {
          folderId: folderId,
          datasetFolder: JSON.stringify(row),
        });
      }

      if (folderId) {
        return history.push(
          `/datasets/dataset-folders/${folderId}/dataset/${row.row?.id}/viewer`,
        );
      }

      return history.push(`/datasets/${row.row?.id}/viewer`);
    },
    [history],
  );

  const {
    mutate: syncRemoteDataset,
    isSuccess: syncRemoteDatasetSuccess,
    isError: syncRemoteDatasetError,
  } = useSyncRemoteDataset();

  useEffect(() => {
    if (syncRemoteDatasetSuccess) {
      NucToast({
        message: `Dataset synced successfully`,
        type: "success",
        hideProgressBar: false,
        //@ts-ignore
        toastId: "sync-dataset",
      });
    }
  }, [syncRemoteDatasetSuccess]);

  useEffect(() => {
    if (syncRemoteDatasetError) {
      NucToast({
        message: `Dataset Sync Failed`,
        type: "error",
        hideProgressBar: false,
        //@ts-ignore
        toastId: "sync-dataset-failed",
      });
    }
  }, [syncRemoteDatasetError]);
  const handleUpdateDataset = useCallback((dataset: any) => {
    setCurrentDataset(dataset);
    setOpenUpdateModal(true);
  }, []);

  const handleClearDataset = useCallback((dataset: any) => {
    setCurrentDataset(dataset);
    setOpenClearModal(true);
  }, []);

  const handleDeleteDataset = useCallback((dataset: any) => {
    setCurrentDataset(dataset);
    setOpenDeleteModal(true);
  }, []);

  const handleOpenPowerBIModal = useCallback((dataset: any) => {
    setCurrentDataset(dataset);
    setOpenPowerBIModal(true);
  }, []);

  const handleChatWithDataset = useCallback(
    (dataset: any) => () => {
      // Set the dataset in the AtomAssist store
      setDropdownDatasets([
        {
          id: dataset.id,
          name: dataset.name,
          description: dataset.description,
        },
      ]);

      // Navigate to AtomAssist with dataset info in state
      history.push({
        pathname: "/assistant",
        state: {
          preloadDataset: {
            id: dataset.id,
            name: dataset.name,
            description: dataset.description,
          },
        },
      });
    },
    [history, setDropdownDatasets],
  );

  const handleChatWithFolder = useCallback(
    (dataset: any) => () => {
      // Get all datasets from the folder
      const folderDatasets = dataset?.datasets || [];

      // Set the datasets in the AtomAssist store
      setDropdownDatasets(
        folderDatasets.map((ds: any) => ({
          id: ds.id,
          name: ds.name,
          description: ds.description,
        })),
      );

      // Navigate to AtomAssist with folder info in state
      history.push({
        pathname: "/assistant",
        state: {
          preloadDatasets: folderDatasets.map((ds: any) => ({
            id: ds.id,
            name: ds.name,
            description: ds.description,
          })),
        },
      });
    },
    [history, setDropdownDatasets],
  );

  const datasetColumns = useMemo<GridColDef<any>[]>(
    () => [
      { field: "id", headerName: "ID" },
      {
        field: "name",
        headerName: "Name",
        flex: 3,
        renderCell: (params: any) => {
          const isFolder = Boolean(
            params.row?.isFolder || params.row?.datasets,
          );
          return (
            <Box
              display="flex"
              flexDirection="row"
              gap={1}
              alignItems="center"
              height="100%"
            >
              {!isFolder && <DatasetStarCell dataset={params.row} />}
              <DatasetTypeChip
                avatar
                type={params.row.dataset_type?.name}
                isFolder={isFolder}
                fontSize={20}
              />
              <Typography variant="body2" fontWeight="bold">
                {params.row?.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2.5,
        hideable: true,
        hide: isSm,
      },
      {
        field: "create_user",
        headerName: "Created by",
        flex: 1,
        // valueGetter: (value: any) => value.create_user,
      },
      {
        field: "create_date",
        headerName: "Create date",
        flex: 1,
        valueFormatter: (value: any) => {
          const date = new Date(value);
          return new Intl.DateTimeFormat(navigator.language, {
            month: "long",
            day: "numeric",
            year: "numeric",
          }).format(date);
        },
      },
      {
        field: "actions",
        type: "actions",
        cellClassName: "dataset-row-actions",
        width: 100,
        align: "right",
        getActions: (params: any) => [
          <Can
            perform="dataset:update"
            yes={() => (
              <Tooltip
                title="Move To Folder"
                placement="bottom"
                arrow
                enterDelay={400}
                hidden={
                  params?.row?.remote_dataset ||
                  params?.row?.isFolder ||
                  atomResults
                }
              >
                <GridActionsCellItem
                  icon={<DriveFileMoveIcon fontSize="large" />}
                  size="small"
                  color="inherit"
                  label="Upload"
                  onClick={() => handleFolderMappingDataset(params.row)}
                  className="render-item"
                  data-testid={`ds-btn-move-${params.row.id}`}
                />
              </Tooltip>
            )}
          />,
          <Can
            perform="dataset:update"
            yes={() => (
              <Tooltip
                title="Upload"
                placement="bottom"
                enterDelay={400}
                hidden={
                  params?.row?.remote_dataset ||
                  params?.row?.isFolder ||
                  // Per-dataset ACL from the search payload: hide when the
                  // flags are present and deny write (absent flags — e.g.
                  // folder rows — must not hide; the upload page enforces
                  // the strict check).
                  params?.row?.my_access?.can_write === false
                }
              >
                <GridActionsCellItem
                  icon={<FileUploadOutlined fontSize="large" />}
                  size="small"
                  color="inherit"
                  label="Upload"
                  onClick={handleUploadToDataset(params.row.id)}
                  className="render-item"
                  data-testid={`ds-btn-upload-${params.row.id}`}
                />
              </Tooltip>
            )}
          />,
          <Can
            perform="dataset_data:read"
            yes={() => (
              <Tooltip
                title="Download"
                placement="bottom"
                enterDelay={400}
                hidden={params?.row?.isFolder}
              >
                <GridActionsCellItem
                  icon={<FileDownloadOutlined fontSize="large" />}
                  size="small"
                  color="inherit"
                  label="Download"
                  onClick={handleDownloadDataset(params.row)}
                  className="render-item"
                  data-testid={`ds-btn-download-${params.row.id}`}
                />
              </Tooltip>
            )}
          />,
          <Can
            perform="dataset_data:read"
            yes={() => (
              <Tooltip
                title="Sync"
                placement="bottom"
                enterDelay={400}
                hidden={!params?.row?.remote_dataset || params?.row?.isFolder}
              >
                <GridActionsCellItem
                  icon={<CloudSync fontSize="large" />}
                  size="small"
                  color="inherit"
                  label="Sync"
                  onClick={() => {
                    syncRemoteDataset(params?.row.id);
                  }}
                  className="render-item"
                  data-testid={`ds-btn-sync-${params.row.id}`}
                />
              </Tooltip>
            )}
          />,
          <Can
            perform="dataset_data:read"
            yes={() => (
              <Tooltip
                title="Power BI"
                placement="bottom"
                enterDelay={400}
                hidden={params?.row?.isFolder}
              >
                <span>
                  <GridActionsCellItem
                    icon={<LaunchOutlined fontSize="large" />}
                    label="Power BI"
                    size="small"
                    color="inherit"
                    hidden={isDocumentType(params.row.dataset_type?.name)}
                    onClick={() => handleOpenPowerBIModal(params.row)}
                    className="render-item"
                    data-testid={`ds-btn-powerbi-${params.row.id}`}
                  />
                </span>
              </Tooltip>
            )}
          />,
          <Can
            perform="dataset_data:read"
            yes={() => (
              <Tooltip
                title="Analyze"
                placement="bottom"
                enterDelay={400}
                hidden={params?.row?.isFolder}
              >
                <span>
                  <GridActionsCellItem
                    icon={<BarChartOutlined fontSize="large" />}
                    label="Analyze"
                    size="small"
                    color="inherit"
                    hidden={isDocumentType(params.row.dataset_type?.name)}
                    onClick={handleAnalyzeClick(params.row.id)}
                    className="render-item"
                    data-testid={`ds-btn-analyze-${params.row.id}`}
                  />
                </span>
              </Tooltip>
            )}
          />,
          <Can
            perform="dataset_data:read"
            yes={() => (
              <Tooltip
                title="Chat"
                placement="bottom"
                enterDelay={400}
                hidden={params?.row?.isFolder}
              >
                <GridActionsCellItem
                  icon={<ChatOutlined fontSize="large" />}
                  size="small"
                  color="inherit"
                  label="Chat"
                  onClick={handleChatWithDataset(params.row)}
                  className="render-item"
                  data-testid={`ds-btn-chat-${params.row.id}`}
                />
              </Tooltip>
            )}
          />,
        ],
      },
      {
        field: "more-actions",
        type: "actions",
        getActions: (params: any) => {
          //* We have to do it this way since wrapping GridActionsCellItem in a <Can /> block doesn't make it show up in the menu
          const canUpdateDatasetData = usePermission(
            "dataset_data:update",
          ).canIt;
          const canReadDatasetData = usePermission("dataset_data:read").canIt;
          const canUpdateDataset = usePermission("dataset:update").canIt;
          const canDeleteDataset = usePermission("dataset:delete").canIt;

          const actions = [];
          if (canUpdateDatasetData && !params?.row?.datasets && !atomResults) {
            actions.push(
              <GridActionsCellItem
                icon={<DriveFileMoveIcon fontSize="small" />}
                label="Move To Folder"
                onClick={() => handleFolderMappingDataset(params.row)}
                showInMenu
              />,
            );
          }

          if (
            canUpdateDatasetData &&
            !params?.row?.remote_dataset &&
            !params?.row?.datasets
          ) {
            actions.push(
              <GridActionsCellItem
                icon={<FileUploadOutlined fontSize="small" />}
                label="Upload"
                onClick={handleUploadToDataset(params.row.id)}
                showInMenu
              />,
            );
          }
          if (params?.row?.remote_dataset && !params?.row?.datasets) {
            actions.push(
              <GridActionsCellItem
                icon={<CloudSync fontSize="small" />}
                label="Sync"
                onClick={() => syncRemoteDataset(params.row.id)}
                showInMenu
              />,
            );
          }
          if (canReadDatasetData && !params?.row?.datasets) {
            actions.push(
              <GridActionsCellItem
                icon={<FileDownloadOutlined fontSize="small" />}
                label="Download"
                onClick={handleDownloadDataset(params.row)}
                showInMenu
              />,
              <GridActionsCellItem
                icon={<LaunchOutlined fontSize="small" />}
                label="Power BI"
                hidden={isDocumentType(params.row.dataset_type?.name)}
                onClick={() => handleOpenPowerBIModal(params.row)}
                showInMenu
              />,
              <GridActionsCellItem
                icon={<BarChartOutlined fontSize="small" />}
                label="Analyze"
                hidden={isDocumentType(params.row.dataset_type?.name)}
                onClick={handleAnalyzeClick(params.row.id)}
                showInMenu
              />,
              <GridActionsCellItem
                icon={<ChatOutlined fontSize="small" />}
                label="Chat"
                onClick={handleChatWithDataset(params.row)}
                showInMenu
                divider
              />,
            );
          }

          if (canUpdateDataset) {
            if (!params?.row?.remote_dataset) {
              actions.push(
                <GridActionsCellItem
                  icon={<EditOutlined fontSize="small" />}
                  label="Edit"
                  onClick={() => handleUpdateDataset(params.row)}
                  showInMenu
                />,
              );
            }
            if (!params?.row?.datasets) {
              actions.push(
                <GridActionsCellItem
                  icon={<BackspaceOutlined fontSize="small" />}
                  label="Clear"
                  onClick={() => handleClearDataset(params.row)}
                  showInMenu
                />,
              );
            }
          }

          if (canDeleteDataset) {
            actions.push(
              <GridActionsCellItem
                icon={<DeleteOutline fontSize="small" />}
                label="Delete"
                onClick={() => handleDeleteDataset(params.row)}
                showInMenu
              />,
            );
          }

          return actions;
        },
      },
    ],
    [
      isSm,
      handleChatWithFolder,
      handleUpdateDataset,
      handleClearDataset,
      handleDeleteDataset,
    ],
  );
  const datasetFolderColumns = useMemo<GridColDef<any>[]>(
    () => [
      { field: "id", headerName: "ID" },
      {
        field: "name",
        headerName: "Name",
        flex: 3,
        renderCell: (params: any) => {
          return (
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              alignItems="center"
              height="100%"
            >
              <DatasetTypeChip
                avatar
                type={params.row.dataset_type?.name}
                isFolder={Boolean(params.row.datasets)}
                fontSize={20}
              />
              <Typography variant="body2" fontWeight="bold">
                {params.row?.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2.5,
        hideable: true,
        hide: isSm,
      },
      {
        field: "create_user",
        headerName: "Created by",
        flex: 1,
        valueGetter: (value: any) => value.username,
      },
      {
        field: "create_date",
        headerName: "Create date",
        flex: 1,
        valueFormatter: (value: any) => {
          const date = new Date(value);
          return new Intl.DateTimeFormat(navigator.language, {
            month: "long",
            day: "numeric",
            year: "numeric",
          }).format(date);
        },
      },
      {
        field: "roles",
        headerName: "Roles",
        flex: 1,
        renderCell: (params: any) => {
          return (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
              gap={1}
            >
              {params.row?.roles?.map((role: any) => (
                <Chip
                  color="primary"
                  label={role.name}
                  key={role.id}
                  size="small"
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        cellClassName: "folder-row-actions",
        width: 100,
        align: "right",
        getActions: (params: any) => [
          <Can
            perform="dataset_data:read"
            yes={() => (
              <Tooltip
                title="Chat with Folder"
                placement="bottom"
                enterDelay={400}
              >
                <GridActionsCellItem
                  icon={<ChatOutlined fontSize="large" />}
                  size="small"
                  color="inherit"
                  label="Chat"
                  onClick={handleChatWithFolder(params.row)}
                  className="render-item"
                  data-testid={`ds-btn-chat-folder-${params.row.id}`}
                />
              </Tooltip>
            )}
          />,
        ],
      },
      {
        field: "more-actions",
        type: "actions",
        getActions: (params: any) => {
          //* We have to do it this way since wrapping GridActionsCellItem in a <Can /> block doesn't make it show up in the menu
          // const canUpdateDatasetData = usePermission(
          //   "dataset_data:update"
          // ).canIt;
          const canReadDatasetData = usePermission("dataset_data:read").canIt;
          const canUpdateDataset = usePermission("dataset:update").canIt;
          const canDeleteDataset = usePermission("dataset:delete").canIt;

          const actions = [];

          // Add Chat with Folder action for folders with read permission
          if (canReadDatasetData) {
            actions.push(
              <GridActionsCellItem
                icon={<ChatOutlined fontSize="small" />}
                label="Chat with Folder"
                onClick={handleChatWithFolder(params.row)}
                showInMenu
              />,
            );
          }

          if (params?.row?.remote_dataset) {
            actions.push(
              <GridActionsCellItem
                icon={<CloudSync fontSize="small" />}
                label="Sync"
                onClick={() => syncRemoteDataset(params.id)}
                showInMenu
              />,
            );
          }

          if (canUpdateDataset) {
            if (!params?.row?.remote_dataset) {
              actions.push(
                <GridActionsCellItem
                  icon={<EditOutlined fontSize="small" />}
                  label="Edit"
                  onClick={() => handleUpdateDataset(params.row)}
                  showInMenu
                />,
              );
            }

            actions.push(
              <GridActionsCellItem
                icon={<BackspaceOutlined fontSize="small" />}
                label="Clear"
                onClick={() => handleClearDataset(params.row)}
                showInMenu
              />,
            );
          }

          if (canDeleteDataset) {
            actions.push(
              <GridActionsCellItem
                icon={<DeleteOutline fontSize="small" />}
                label="Delete"
                onClick={() => handleDeleteDataset(params.row)}
                showInMenu
              />,
            );
          }

          return actions;
        },
      },
    ],
    [
      isSm,
      handleChatWithFolder,
      handleUpdateDataset,
      handleClearDataset,
      handleDeleteDataset,
    ],
  );

  useEffect(() => {
    const newColumnVisibilityModel = {
      id: false,
      description: !isLg,
      actions: !isLg,
      create_user: !isXl,
      visibility_level: !isXl,
      create_date: !isSm,
      // Ensure more-actions is always visible (we control visibility with CSS)
      "more-actions": true,
    };
    setColumnVisibilityModel(newColumnVisibilityModel);
  }, [isSm, isMd, isLg, isXl]);

  return (
    <>
      <DataGridSearch
        columns={datasetColumns}
        datasetFolderColumns={datasetFolderColumns}
        queryKey={"datasetList"}
        queryFn={searchDatasets}
        onRowClick={navigateToDataset}
        filterComponent={DatasetFilters}
        atomResults={atomResults}
        columnVisibilityModel={columnVisibilityModel}
        noRowsOverlay={(props) => {
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <CustomNoRowsOverlay
                message={
                  !props.isFolder
                    ? "No datasets found"
                    : "No dataset folders found"
                }
              />
              <Box mt={2}>
                {!props.isFolder ? (
                  <Can
                    perform="dataset:write"
                    yes={() => (
                      <CreateItemButton
                        itemName="Create dataset"
                        createItemModal={CreateDatasetModal}
                      />
                    )}
                  />
                ) : (
                  <Can
                    perform="dataset:write"
                    yes={() => (
                      <CreateItemButton
                        itemName="Create dataset folder"
                        createItemModal={CreateDatasetFolderModal}
                      />
                    )}
                  />
                )}
              </Box>
            </Box>
          );
        }}
      />
      <UpdateDatasetModal
        id={currentDataset?.id}
        datasetName={currentDataset?.name}
        datasetDescription={currentDataset?.description}
        datasetTags={currentDataset?.tags}
        isVisible={openUpdateModal && !currentDataset?.datasets}
        onClose={() => setOpenUpdateModal(false)}
      />
      <UpdateDatasetFolderModal
        folderId={currentDataset?.id}
        dataset={currentDataset}
        isVisible={openUpdateModal && currentDataset?.datasets}
        onClose={() => setOpenUpdateModal(false)}
      />
      <ClearDatasetModal
        id={currentDataset?.id}
        dataset={currentDataset}
        isFolder={Boolean(currentDataset?.datasets)}
        isVisible={openClearModal}
        datasetName={currentDataset?.name}
        onClose={() => setOpenClearModal(false)}
      />
      <DeleteDatasetModal
        id={currentDataset?.id}
        isVisible={openDeleteModal}
        isFolder={Boolean(currentDataset?.datasets)}
        datasetName={currentDataset?.name}
        onClose={() => setOpenDeleteModal(false)}
      />
      <PowerBIDataModal
        url={`${nuclearn_api_url}/datasets/${currentDataset?.id}/data`}
        message={` to dataset "${currentDataset?.name}"`}
        forPage={"datasets"}
        isVisible={openPowerBIModal}
        onClose={() => setOpenPowerBIModal(false)}
      />
      <DatasetFolderMappingModal
        id={currentDataset?.id}
        isVisible={openFolderMappingModal}
        datasetName={currentDataset?.name}
        onClose={() => setOpenFolderMappingModal(false)}
      />
    </>
  );
};
export default DatasetTableSearch;
